import React, { useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Building2, Newspaper, Inbox, LogOut, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const LINKS = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/properties', label: 'Properties', icon: Building2 },
  { to: '/admin/blog', label: 'Blog Posts', icon: Newspaper },
  { to: '/admin/leads', label: 'Leads', icon: Inbox },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('cespedis_user') || '{}');

  useEffect(() => { document.title = 'Cespedis Admin'; }, []);

  const logout = () => {
    localStorage.removeItem('cespedis_token');
    localStorage.removeItem('cespedis_user');
    navigate('/admin/login');
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden lg:flex w-64 flex-col bg-[#144726] text-white">
        <Link to="/" className="flex items-center gap-2 border-b border-white/10 px-6 py-5">
          <img src="/brand/cespedis-logo-nav.png" alt="Cespedis" className="h-9 w-auto brightness-0 invert opacity-95" />
          <span className="ml-2 text-[10px] tracking-[0.18em] uppercase text-white/60">Admin</span>
        </Link>
        <nav className="flex-1 px-3 py-5">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`admin-nav-${l.label.toLowerCase().replace(/ /g, '-')}`} className={({ isActive }) => cn('mb-1 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors', isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white')}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <Link to="/" className="mb-2 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 hover:bg-white/5"><Globe className="h-4 w-4" /> View Public Site</Link>
          <button onClick={logout} data-testid="admin-logout-button" className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 hover:bg-white/5"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>

      <div className="flex w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:px-8">
          <div className="lg:hidden flex items-center gap-2">
            <img src="/brand/cespedis-logo-nav.png" alt="Cespedis" className="h-8 w-auto" />
            <span className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Admin</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-500">{user?.email || 'admin@cespedis.com'}</span>
            <Button size="sm" variant="outline" onClick={logout} className="border-slate-200 text-slate-700" data-testid="admin-topbar-logout"><LogOut className="mr-2 h-4 w-4" /> Logout</Button>
          </div>
        </header>

        {/* Mobile bottom nav */}
        <nav className="flex lg:hidden border-b border-slate-200 bg-white px-2">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => cn('flex flex-1 flex-col items-center gap-1 py-3 text-[11px] font-semibold', isActive ? 'text-[#144726]' : 'text-slate-500')}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
